import { useEffect, useState } from 'react';
import { createClient } from '@supabase/supabase-js';
import JobsList from './components/JobsList';
import ActiveJob from './components/ActiveJob';
import MessagingPanel from './components/MessagingPanel';
import EarningsOverview from './components/EarningsOverview';
import ProfileSettings from './components/ProfileSettings';
import WorkwearShop from './components/WorkwearShop';
import SubscriptionPaymentSection from './components/SubscriptionPaymentSection';
import AvailableJobs from './components/AvailableJobs';
import JobManagement from './components/JobManagement';
import SupportChat from './components/SupportChat';
import InvoicesPanel from './components/InvoicesPanel';
import NotificationsPanel from './components/NotificationsPanel';
import PayoutManagement from './components/PayoutManagement';
import ProviderStatusBanner from '../../components/feature/ProviderStatusBanner';
import VanAccessModule from './components/VanAccessModule';
import DocumentsVerificationPanel from './components/DocumentsVerificationPanel';
import TransportVerification from './components/TransportVerification';
import { useNavigate, Link } from 'react-router-dom';

const supabase = createClient(
  import.meta.env.VITE_PUBLIC_SUPABASE_URL,
  import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY
);

export default function ProviderDashboard() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('overview');
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [provider, setProvider] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [unreadMessages, setUnreadMessages] = useState(0);
  const [activeJob, setActiveJob] = useState<any>(null);
  const [notifications, setNotifications] = useState<any[]>([]);
  const [user, setUser] = useState<any>(null);
  const [providerProfile, setProviderProfile] = useState<any>(null);

  const tabs = [
    { id: 'available', label: 'Available Jobs', icon: 'ri-briefcase-line' },
    { id: 'active', label: 'Active Jobs', icon: 'ri-time-line' },
    { id: 'jobs', label: 'Job Management', icon: 'ri-list-check' },
    { id: 'earnings', label: 'Earnings', icon: 'ri-money-pound-circle-line' },
    { id: 'invoices', label: 'Invoices', icon: 'ri-file-list-3-line' },
    { id: 'messaging', label: 'Messages', icon: 'ri-message-3-line' },
    { id: 'workwear', label: 'Workwear Shop', icon: 'ri-shirt-line' },
    { id: 'profile', label: 'Profile', icon: 'ri-user-settings-line' },
    { id: 'support', label: 'Support', icon: 'ri-customer-service-2-line' },
  ];

  useEffect(() => {
    // Set document title and meta tags
    document.title = 'Provider Dashboard | Manage Your Jobs - Taskbudy';
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Manage your Taskbudy provider account. View available jobs, active bookings, earnings, payouts, and profile settings.');
    }
    const metaRobots = document.querySelector('meta[name="robots"]');
    if (metaRobots) {
      metaRobots.setAttribute('content', 'noindex, nofollow');
    }
  }, []);

  useEffect(() => {
    loadProviderData();
  }, []);

  useEffect(() => {
    if (provider) {
      loadUnreadCount();
      checkActiveJob();
      loadNotifications();
      
      // Subscribe to new messages
      const channel = supabase
        .channel('provider_support_messages')
        .on(
          'postgres_changes',
          {
            event: 'INSERT',
            schema: 'public',
            table: 'admin_messages',
            filter: `recipient_id=eq.${provider.user_id}`
          },
          () => {
            loadUnreadCount();
          }
        )
        .subscribe();

      return () => {
        supabase.removeChannel(channel);
      };
    }
  }, [provider]);

  const loadUnreadCount = async () => {
    if (!provider) return;
    
    const { count } = await supabase
      .from('admin_messages')
      .select('*', { count: 'exact', head: true })
      .eq('recipient_id', provider.user_id)
      .eq('recipient_type', 'provider')
      .eq('is_read', false);

    setUnreadMessages(count || 0);
  };

  const checkActiveJob = async () => {
    if (!provider) return;
    
    const { data } = await supabase
      .from('jobs')
      .select('*')
      .eq('provider_id', provider.user_id)
      .in('status', ['accepted', 'in_progress'])
      .single();

    setActiveJob(data);
  };

  const loadNotifications = async () => {
    if (!provider) return;
    
    const { data } = await supabase
      .from('notifications')
      .select('*')
      .eq('user_id', provider.user_id)
      .order('created_at', { ascending: false })
      .limit(10);

    setNotifications(data || []);
  };

  const loadProviderData = async () => {
    const { data: { user: authUser } } = await supabase.auth.getUser();
    if (!authUser) {
      navigate('/auth/signin');
      return;
    }

    setUser(authUser);

    // Check if user has a provider profile
    const { data: providerData } = await supabase
      .from('provider_profiles')
      .select('*')
      .eq('user_id', authUser.id)
      .single();

    if (!providerData) {
      navigate('/provider-signup');
      return;
    }

    setProvider(providerData);
    setLoading(false);
  };

  const loadProviderProfile = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data, error } = await supabase
        .from('provider_profiles')
        .select('*')
        .eq('user_id', user.id)
        .single();

      if (error) throw error;
      setProviderProfile(data);
    } catch (error) {
      console.error('Error loading provider profile:', error);
    }
  };

  const handleLogout = async () => {
    await supabase.auth.signOut();
    navigate('/');
  };

  const handleSignOut = async () => {
    await supabase.auth.signOut();
    navigate('/');
  };

  const checkUser = () => {
    loadProviderData();
  };

  const handleNotificationAction = (action: string) => {
    setActiveTab(action);
    setSidebarOpen(false);
  };

  const renderContent = () => {
    // Transport Verification Route
    if (activeTab === 'transport-verification') {
      return <TransportVerification />;
    }

    if (loading) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-gray-50">
          <div className="text-center">
            <i className="ri-loader-4-line text-4xl text-teal-600 animate-spin"></i>
            <p className="mt-4 text-gray-600">Loading dashboard...</p>
          </div>
        </div>
      );
    }

    if (!provider) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-gray-50">
          <div className="text-center max-w-md mx-auto p-8">
            <i className="ri-lock-line text-6xl text-gray-400 mb-4"></i>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Authentication Required</h2>
            <p className="text-gray-600 mb-6">Please sign in to access your provider dashboard</p>
            <button
              onClick={() => navigate('/auth/signin')}
              className="inline-block px-6 py-3 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors whitespace-nowrap cursor-pointer"
            >
              Sign In
            </button>
          </div>
        </div>
      );
    }

    // Check application status
    const isApplicationPending = provider.application_status === 'pending';
    const isApplicationApproved = provider.application_status === 'approved' || provider.status === 'approved' || provider.status === 'active';
    const isPaymentPending = isApplicationApproved && !provider.subscription_paid;
    const isWorkwearPending = isApplicationApproved && provider.subscription_paid && !provider.workwear_purchased;
  
    // Check provider_status for new flow
    const needsPayment = provider.provider_status === 'payment_required';
    const needsDocuments = provider.provider_status === 'subscription_paid_pending_verification' || provider.provider_status === 'verification_pending';
    const needsWorkwear = provider.provider_status === 'approved_needs_workwear';
    const isActive = provider.provider_status === 'active';

    // Check if documents are uploaded
    const hasRequiredDocuments = provider.selfie_photo && provider.photo_id;
    const hasOptionalDocuments = provider.cscs_card && provider.dbs_check && provider.public_liability_insurance;

    // Provider is fully active ONLY if: approved, paid, workwear purchased, and all documents uploaded
    const isFullyActive = isApplicationApproved && provider.subscription_paid && provider.workwear_purchased && hasRequiredDocuments && hasOptionalDocuments && (provider.status === 'active' || provider.is_active);

    // Check if documents are pending
    const isDocumentsPending = !hasRequiredDocuments || !hasOptionalDocuments;

    // Calculate hours remaining for payment
    const getPaymentHoursRemaining = () => {
      if (!provider.payment_deadline) return 0;
      const deadline = new Date(provider.payment_deadline);
      const now = new Date();
      const hoursRemaining = Math.floor((deadline.getTime() - now.getTime()) / (1000 * 60 * 60));
      return Math.max(0, hoursRemaining);
    };

    const paymentHoursRemaining = getPaymentHoursRemaining();

    return (
      <div className="min-h-screen bg-gray-50 overflow-x-hidden">
        {/* Top Navigation */}
        <nav className="bg-white border-b border-gray-200 sticky top-0 z-50 animate-[slideDown_0.5s_ease-out]">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex justify-between items-center h-16">
              <div className="flex items-center gap-2 sm:gap-4">
                <button
                  onClick={() => setSidebarOpen(!sidebarOpen)}
                  className="lg:hidden w-10 h-10 flex items-center justify-center text-gray-700 hover:text-teal-600 transition-all duration-300 cursor-pointer transform hover:scale-110"
                  aria-label="Toggle sidebar"
                >
                  <i className="ri-menu-line text-2xl"></i>
                </button>
                <Link to="/" className="flex items-center">
                  <img 
                    src="https://static.readdy.ai/image/a3382d87126fc476a032aa8f5ebcbb38/dc77c2be5a78759d16f1acf02198b697.png" 
                    alt="TaskBudy" 
                    className="h-8 md:h-10"
                  />
                </Link>
                <span className="hidden sm:inline-block text-sm font-semibold text-gray-700">Provider Dashboard</span>
              </div>

              {/* Mobile Menu Button */}
              <button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="lg:hidden w-10 h-10 flex items-center justify-center text-gray-700 hover:text-teal-600 transition-all duration-300 cursor-pointer transform hover:scale-110"
                aria-label="Toggle menu"
              >
                <i className={`${mobileMenuOpen ? 'ri-close-line' : 'ri-more-2-fill'} text-2xl transition-transform duration-300 ${mobileMenuOpen ? 'rotate-90' : ''}`}></i>
              </button>

              {/* Desktop Navigation */}
              <div className="hidden lg:flex items-center gap-4">
                <button 
                  onClick={() => setActiveTab('support')}
                  className="w-10 h-10 flex items-center justify-center text-gray-700 hover:text-teal-600 transition-all duration-300 cursor-pointer relative transform hover:scale-110"
                >
                  <i className="ri-customer-service-2-line text-xl"></i>
                  {unreadMessages > 0 && (
                    <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center animate-pulse">
                      {unreadMessages}
                    </span>
                  )}
                </button>
                <button
                  onClick={() => handleLogout()}
                  className="px-4 py-2 text-red-600 hover:bg-red-50 rounded-lg transition-all duration-300 whitespace-nowrap cursor-pointer font-medium transform hover:scale-105"
                >
                  Logout
                </button>
              </div>
            </div>

            {/* Mobile Menu Dropdown */}
            {mobileMenuOpen && (
              <div className="lg:hidden border-t border-gray-200 py-4 animate-[fadeIn_0.3s_ease-out]">
                <div className="flex flex-col gap-3">
                  <button 
                    onClick={() => {
                      setActiveTab('support');
                      setMobileMenuOpen(false);
                    }}
                    className="px-4 py-3 text-gray-700 hover:bg-gray-50 font-medium transition-all duration-300 whitespace-nowrap cursor-pointer rounded-lg text-left flex items-center gap-3 transform hover:translate-x-2"
                  >
                    <i className="ri-customer-service-2-line text-xl"></i>
                    <span>Live Support</span>
                    {unreadMessages > 0 && (
                      <span className="ml-auto w-5 h-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center animate-pulse">
                        {unreadMessages}
                      </span>
                    )}
                  </button>
                  <button
                    onClick={() => {
                      handleLogout();
                      setMobileMenuOpen(false);
                    }}
                    className="px-4 py-3 text-red-600 hover:bg-red-50 font-medium transition-all duration-300 whitespace-nowrap cursor-pointer rounded-lg text-left flex items-center gap-3 transform hover:translate-x-2"
                  >
                    <i className="ri-logout-box-line text-xl"></i>
                    <span>Logout</span>
                  </button>
                </div>
              </div>
            )}
          </div>
        </nav>

        {/* Provider Status Banner - Show activation status */}
        {provider && (
          <ProviderStatusBanner
            status={{
              is_approved: provider.is_approved || provider.application_status === 'approved',
              is_active: provider.is_active || provider.status === 'active',
              subscription_status: provider.subscription_paid ? 'active' : 'inactive',
              workwear_purchased: provider.workwear_purchased,
              documents_uploaded: provider.selfie_photo && provider.photo_id && provider.cscs_card && provider.dbs_check && provider.public_liability_insurance,
              verification_status: provider.verification_status,
            }}
          />
        )}

        <div className="flex">
          {/* Sidebar Navigation */}
          <aside className={`${sidebarOpen ? 'translate-x-0' : '-translate-x-full'} lg:translate-x-0 fixed lg:sticky top-16 left-0 w-64 bg-white border-r border-gray-200 h-[calc(100vh-4rem)] transition-transform duration-300 z-40 overflow-y-auto`}>
            <nav className="p-4 space-y-2">
              <button
                onClick={() => {
                  setActiveTab('overview');
                  setSidebarOpen(false);
                }}
                className={`w-full flex items-center space-x-3 px-4 py-3 rounded-lg transition-colors cursor-pointer whitespace-nowrap ${
                  activeTab === 'overview'
                    ? 'bg-teal-50 text-teal-600'
                    : 'text-gray-700 hover:bg-gray-50'
                }`}
              >
                <i className="ri-dashboard-line text-xl w-6 h-6 flex items-center justify-center"></i>
                <span className="font-medium">Overview</span>
              </button>

              <button
                onClick={() => {
                  setActiveTab('notifications');
                  setSidebarOpen(false);
                }}
                className={`w-full flex items-center space-x-3 px-4 py-3 rounded-lg transition-colors cursor-pointer whitespace-nowrap ${
                  activeTab === 'notifications'
                    ? 'bg-teal-50 text-teal-600'
                    : 'text-gray-700 hover:bg-gray-50'
                }`}
              >
                <i className="ri-notification-3-line text-xl w-6 h-6 flex items-center justify-center"></i>
                <span className="font-medium">Notifications</span>
                {!isFullyActive && (
                  <span className="ml-auto w-2 h-2 bg-red-500 rounded-full animate-pulse"></span>
                )}
              </button>

              <button
                onClick={() => {
                  setActiveTab('available-jobs');
                  setSidebarOpen(false);
                }}
                className={`w-full flex items-center space-x-3 px-4 py-3 rounded-lg transition-colors cursor-pointer whitespace-nowrap ${
                  activeTab === 'available-jobs'
                    ? 'bg-teal-50 text-teal-600'
                    : 'text-gray-700 hover:bg-gray-50'
                }`}
                disabled={!isFullyActive}
              >
                <i className="ri-search-line text-xl w-6 h-6 flex items-center justify-center"></i>
                <span className="font-medium">Available Jobs</span>
                {!isFullyActive && (
                  <i className="ri-lock-line text-sm ml-auto"></i>
                )}
              </button>

              <button
                onClick={() => {
                  setActiveTab('active-job');
                  setSidebarOpen(false);
                }}
                className={`w-full flex items-center space-x-3 px-4 py-3 rounded-lg transition-colors cursor-pointer whitespace-nowrap ${
                  activeTab === 'active-job'
                    ? 'bg-teal-50 text-teal-600'
                    : 'text-gray-700 hover:bg-gray-50'
                }`}
                disabled={!isFullyActive}
              >
                <i className="ri-tools-line text-xl w-6 h-6 flex items-center justify-center"></i>
                <span className="font-medium">Active Job</span>
                {activeJob && (
                  <span className="ml-auto bg-green-500 text-white text-xs px-2 py-1 rounded-full whitespace-nowrap">
                    In Progress
                  </span>
                )}
                {!isFullyActive && (
                  <i className="ri-lock-line text-sm ml-auto"></i>
                )}
              </button>

              <button
                onClick={() => {
                  setActiveTab('messages');
                  setSidebarOpen(false);
                }}
                className={`w-full flex items-center space-x-3 px-4 py-3 rounded-lg transition-colors cursor-pointer whitespace-nowrap ${
                  activeTab === 'messages'
                    ? 'bg-teal-50 text-teal-600'
                    : 'text-gray-700 hover:bg-gray-50'
                }`}
              >
                <i className="ri-message-3-line text-xl w-6 h-6 flex items-center justify-center"></i>
                <span className="font-medium">Client Messages</span>
              </button>

              <button
                onClick={() => {
                  setActiveTab('earnings');
                  setSidebarOpen(false);
                }}
                className={`w-full flex items-center space-x-3 px-4 py-3 rounded-lg transition-colors cursor-pointer whitespace-nowrap ${
                  activeTab === 'earnings'
                    ? 'bg-teal-50 text-teal-600'
                    : 'text-gray-700 hover:bg-gray-50'
                }`}
                disabled={!isFullyActive}
              >
                <i className="ri-money-pound-circle-line text-xl w-6 h-6 flex items-center justify-center"></i>
                <span className="font-medium">Earnings</span>
                {!isFullyActive && (
                  <i className="ri-lock-line text-sm ml-auto"></i>
                )}
              </button>

              <button
                onClick={() => {
                  setActiveTab('payouts');
                  setSidebarOpen(false);
                }}
                className={`w-full flex items-center space-x-3 px-4 py-3 rounded-lg transition-colors cursor-pointer whitespace-nowrap ${
                  activeTab === 'payouts'
                    ? 'bg-teal-50 text-teal-600'
                    : 'text-gray-700 hover:bg-gray-50'
                }`}
                disabled={!isFullyActive}
              >
                <i className="ri-bank-line text-xl w-6 h-6 flex items-center justify-center"></i>
                <span className="font-medium">Payouts</span>
                {!isFullyActive && (
                  <i className="ri-lock-line text-sm ml-auto"></i>
                )}
              </button>

              <button
                onClick={() => {
                  setActiveTab('workwear');
                  setSidebarOpen(false);
                }}
                className={`w-full flex items-center space-x-3 px-4 py-3 rounded-lg transition-colors cursor-pointer whitespace-nowrap ${
                  activeTab === 'workwear'
                    ? 'bg-teal-50 text-teal-600'
                    : 'text-gray-700 hover:bg-gray-50'
                }`}
              >
                <i className="ri-shopping-bag-3-line text-xl w-6 h-6 flex items-center justify-center"></i>
                <span className="font-medium">Workwear Shop</span>
              </button>

              <button
                onClick={() => {
                  setActiveTab('profile');
                  setSidebarOpen(false);
                }}
                className={`w-full flex items-center space-x-3 px-4 py-3 rounded-lg transition-colors cursor-pointer whitespace-nowrap ${
                  activeTab === 'profile'
                    ? 'bg-teal-50 text-teal-600'
                    : 'text-gray-700 hover:bg-gray-50'
                }`}
              >
                <i className="ri-user-settings-line text-xl w-6 h-6 flex items-center justify-center"></i>
                <span className="font-medium">Profile</span>
              </button>

              <button
                onClick={() => {
                  setActiveTab('documents');
                  setSidebarOpen(false);
                }}
                className={`w-full flex items-center space-x-3 px-4 py-3 rounded-lg transition-colors cursor-pointer whitespace-nowrap ${
                  activeTab === 'documents'
                    ? 'bg-teal-50 text-teal-600'
                    : 'text-gray-700 hover:bg-gray-50'
                }`}
              >
                <i className="ri-file-upload-line text-xl w-6 h-6 flex items-center justify-center"></i>
                <span className="font-medium">Documents</span>
                {isDocumentsPending && (
                  <i className="ri-alert-fill text-orange-500 ml-auto animate-pulse"></i>
                )}
              </button>

              <button
                onClick={() => {
                  setActiveTab('support');
                  setSidebarOpen(false);
                }}
                className={`w-full flex items-center space-x-3 px-4 py-3 rounded-lg transition-colors cursor-pointer whitespace-nowrap ${
                  activeTab === 'support'
                    ? 'bg-teal-50 text-teal-600'
                    : 'text-gray-700 hover:bg-gray-50'
                }`}
              >
                <i className="ri-customer-service-2-line text-xl w-6 h-6 flex items-center justify-center"></i>
                <span className="font-medium">Live Support</span>
                {unreadMessages > 0 && (
                  <span className="ml-auto w-5 h-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center animate-pulse">
                    {unreadMessages}
                  </span>
                )}
              </button>

              <button
                onClick={handleSignOut}
                className="w-full flex items-center space-x-3 px-4 py-3 rounded-lg text-red-600 hover:bg-red-50 transition-colors cursor-pointer whitespace-nowrap lg:hidden"
              >
                <i className="ri-logout-box-line text-xl w-6 h-6 flex items-center justify-center"></i>
                <span className="font-medium">Sign Out</span>
              </button>
            </nav>
          </aside>

          {/* Overlay for mobile sidebar */}
          {sidebarOpen && (
            <div 
              className="fixed inset-0 bg-black/50 z-30 lg:hidden"
              onClick={() => setSidebarOpen(false)}
            ></div>
          )}

          {/* Main Content */}
          <main className="flex-1 p-4 sm:p-6 w-full lg:w-[calc(100%-16rem)]">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
              
              {activeTab === 'overview' && (
                <div className="space-y-4 sm:space-y-6">
                  <div>
                    <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Welcome back, {provider.first_name}!</h1>
                    <p className="text-sm sm:text-base text-gray-600 mt-1">Here's your application status and dashboard overview</p>
                  </div>

                  {/* Application Progress */}
                  <div className="bg-white rounded-xl shadow-sm p-4 sm:p-6">
                    <h2 className="text-lg sm:text-xl font-bold text-gray-900 mb-4 sm:mb-6">Application Progress</h2>
                    <div className="space-y-3 sm:space-y-4">
                      {/* Step 1: Application Submitted */}
                      <div className="flex items-start gap-4">
                        <div className="w-10 h-10 rounded-full flex items-center justify-center bg-green-500 text-white">
                          <i className="ri-check-line text-xl"></i>
                        </div>
                        <div className="flex-1">
                          <h3 className="font-bold text-gray-900">Application Submitted</h3>
                          <p className="text-sm text-gray-600">Your application has been submitted successfully ✓</p>
                        </div>
                      </div>

                      {/* Step 2: Payment */}
                      <div className="flex items-start gap-4">
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                          needsPayment ? 'bg-orange-500 animate-pulse' : 
                          !needsPayment ? 'bg-green-500' : 'bg-gray-300'
                        } text-white`}>
                          {needsPayment ? (
                            <i className="ri-alert-line text-xl"></i>
                          ) : !needsPayment ? (
                            <i className="ri-check-line text-xl"></i>
                          ) : (
                            <span className="text-sm font-bold">2</span>
                          )}
                        </div>
                        <div className="flex-1">
                          <h3 className="font-bold text-gray-900">Subscription Payment</h3>
                          <p className="text-sm text-gray-600">
                            {needsPayment 
                              ? 'Payment required - Annual subscription £299'
                              : 'Payment completed ✓'
                            }
                          </p>
                          {needsPayment && (
                            <button
                              onClick={() => setActiveTab('payment')}
                              className="mt-2 px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors font-medium whitespace-nowrap cursor-pointer text-sm"
                            >
                              Complete Payment Now
                            </button>
                          )}
                        </div>
                      </div>

                      {/* Step 3: Documents Upload */}
                      <div className="flex items-start gap-4">
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                          needsDocuments ? 'bg-orange-500 animate-pulse' : 
                          needsWorkwear || isActive ? 'bg-green-500' : 'bg-gray-300'
                        } text-white`}>
                          {needsDocuments ? (
                            <i className="ri-alert-line text-xl"></i>
                          ) : needsWorkwear || isActive ? (
                            <i className="ri-check-line text-xl"></i>
                          ) : (
                            <span className="text-sm font-bold">3</span>
                          )}
                        </div>
                        <div className="flex-1">
                          <h3 className="font-bold text-gray-900">Document Verification</h3>
                          <p className="text-sm text-gray-600">
                            {needsDocuments 
                              ? 'Upload required documents for verification'
                              : needsWorkwear || isActive
                              ? 'All documents verified ✓'
                              : 'Complete payment first'
                            }
                          </p>
                          {needsDocuments && (
                            <button
                              onClick={() => setActiveTab('documents')}
                              className="mt-2 px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors font-medium whitespace-nowrap cursor-pointer text-sm"
                            >
                              Upload Documents
                            </button>
                          )}
                        </div>
                      </div>

                      {/* Step 4: Workwear Purchase */}
                      <div className="flex items-start gap-4">
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                          needsWorkwear ? 'bg-red-500 animate-pulse' : 
                          isActive ? 'bg-green-500' : 'bg-gray-300'
                        } text-white`}>
                          {needsWorkwear ? (
                            <i className="ri-alert-line text-xl"></i>
                          ) : isActive ? (
                            <i className="ri-check-line text-xl"></i>
                          ) : (
                            <span className="text-sm font-bold">4</span>
                          )}
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <h3 className="font-bold text-gray-900">Workwear Purchase</h3>
                            <span className="px-2 py-0.5 bg-red-100 text-red-700 text-xs font-bold rounded-full">MANDATORY</span>
                          </div>
                          <p className="text-sm text-gray-600">
                            {needsWorkwear 
                              ? 'Purchase required workwear kit (£108)'
                              : isActive
                              ? 'Workwear purchased ✓'
                              : 'Complete verification first'
                            }
                          </p>
                          {needsWorkwear && (
                            <button
                              onClick={() => setActiveTab('workwear')}
                              className="mt-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors font-bold whitespace-nowrap cursor-pointer text-sm animate-pulse"
                            >
                              Purchase Workwear Kit
                            </button>
                          )}
                        </div>
                      </div>

                      {/* Step 5: Active */}
                      <div className="flex items-start gap-4">
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                          isActive ? 'bg-green-500' : 'bg-gray-300'
                        } text-white`}>
                          {isActive ? (
                            <i className="ri-check-line text-xl"></i>
                          ) : (
                            <span className="text-sm font-bold">5</span>
                          )}
                        </div>
                        <div className="flex-1">
                          <h3 className="font-bold text-gray-900">Account Active</h3>
                          <p className="text-sm text-gray-600">
                            {isActive 
                              ? 'Your account is fully active! Start accepting jobs.'
                              : 'Complete all steps above to activate your account'
                            }
                          </p>
                          {isActive && (
                            <button
                              onClick={() => setActiveTab('available-jobs')}
                              className="mt-2 px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors font-medium whitespace-nowrap cursor-pointer text-sm"
                            >
                              View Available Jobs
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Quick Stats */}
                  {isFullyActive && (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
                      <div className="bg-white rounded-xl shadow-sm p-4 sm:p-6">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-xs sm:text-sm text-gray-600">Active Jobs</p>
                            <p className="text-2xl sm:text-3xl font-bold text-gray-900 mt-1">{activeJob ? '1' : '0'}</p>
                          </div>
                          <div className="w-10 h-10 sm:w-12 sm:h-12 bg-teal-100 rounded-full flex items-center justify-center flex-shrink-0">
                            <i className="ri-briefcase-line text-xl sm:text-2xl text-teal-600"></i>
                          </div>
                        </div>
                      </div>

                      <div className="bg-white rounded-xl shadow-sm p-4 sm:p-6">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-xs sm:text-sm text-gray-600">Total Earnings</p>
                            <p className="text-2xl sm:text-3xl font-bold text-gray-900 mt-1">£0</p>
                          </div>
                          <div className="w-10 h-10 sm:w-12 sm:h-12 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                            <i className="ri-money-pound-circle-line text-xl sm:text-2xl text-green-600"></i>
                          </div>
                        </div>
                      </div>

                      <div className="bg-white rounded-xl shadow-sm p-4 sm:p-6">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-xs sm:text-sm text-gray-600">Completed Jobs</p>
                            <p className="text-2xl sm:text-3xl font-bold text-gray-900 mt-1">0</p>
                          </div>
                          <div className="w-10 h-10 sm:w-12 sm:h-12 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                            <i className="ri-check-double-line text-xl sm:text-2xl text-blue-600"></i>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Van Access Module - Add after Quick Stats */}
                  {isFullyActive && (
                    <div>
                      <h2 className="text-xl font-bold text-gray-900 mb-4">Additional Services</h2>
                      <VanAccessModule />
                    </div>
                  )}

                  {/* Recent Notifications */}
                  {notifications.length > 0 && (
                    <div className="bg-white rounded-xl shadow-sm p-4 sm:p-6">
                      <h2 className="text-xl font-bold text-gray-900 mb-4">Recent Notifications</h2>
                      <div className="space-y-3">
                        {notifications.slice(0, 3).map((notif) => (
                          <div key={notif.id} className="flex items-start gap-3 p-3 bg-blue-50 rounded-lg">
                            <i className="ri-notification-3-fill text-blue-600 text-xl mt-1"></i>
                            <div className="flex-1">
                              <h3 className="font-semibold text-gray-900">{notif.title}</h3>
                              <p className="text-sm text-gray-600">{notif.message}</p>
                              <p className="text-xs text-gray-500 mt-1">
                                {new Date(notif.created_at).toLocaleString()}
                              </p>
                            </div>
                          </div>
                        ))}
                      </div>
                      <button
                        onClick={() => setActiveTab('notifications')}
                        className="mt-4 text-teal-600 hover:text-teal-700 font-medium text-sm cursor-pointer"
                      >
                        View All Notifications →
                      </button>
                    </div>
                  )}
                </div>
              )}
              
              {activeTab === 'available-jobs' && isFullyActive && <AvailableJobs providerId={provider.id} />}
              {activeTab === 'active-job' && isFullyActive && <JobManagement providerId={provider.id} />}
              {activeTab === 'messages' && (
                <div>
                  <div className="mb-6">
                    <h2 className="text-2xl font-bold text-gray-900">Client Messages</h2>
                    <p className="text-gray-600 mt-1">Chat with clients about active jobs</p>
                  </div>
                  <MessagingPanel userId={user?.id} />
                </div>
              )}
              {activeTab === 'earnings' && isFullyActive && <EarningsOverview userId={user?.id} />}
              {activeTab === 'payouts' && isFullyActive && (
                <div>
                  <div className="mb-6">
                    <h2 className="text-2xl font-bold text-gray-900">Payouts & Withdrawals</h2>
                    <p className="text-gray-600 mt-1">Manage your earnings and request payouts</p>
                  </div>
                  <PayoutManagement userId={user?.id} />
                </div>
              )}
              {activeTab === 'invoices' && <InvoicesPanel />}
              {activeTab === 'workwear' && (
                <div>
                  <div className="mb-6">
                    <h2 className="text-2xl font-bold text-gray-900">Workwear Shop</h2>
                    {isWorkwearPending && (
                      <div className="mt-3 bg-red-50 border-2 border-red-200 rounded-lg p-4">
                        <div className="flex items-start gap-3">
                          <i className="ri-alert-line text-red-600 text-xl"></i>
                          <div>
                            <p className="font-bold text-red-900 mb-1">⚠️ Workwear Purchase Required</p>
                            <p className="text-sm text-red-800">
                              You must purchase the starter workwear kit before you can accept jobs. All Taskbudy providers wear branded workwear to maintain professional standards and build customer trust.
                            </p>
                          </div>
                        </div>
                      </div>
                    )}
                    {provider.workwear_purchased && (
                      <p className="text-gray-600 mt-1">Order additional workwear items</p>
                    )}
                  </div>
                  <WorkwearShop provider={provider} onPurchaseComplete={checkUser} />
                </div>
              )}
              {activeTab === 'profile' && <ProfileSettings user={user} provider={provider} />}
              {activeTab === 'documents' && (
                <div>
                  <div className="mb-6">
                    <h2 className="text-2xl font-bold text-gray-900">Document Verification</h2>
                    <p className="text-gray-600 mt-1">Upload required documents for your {provider.trade_type} application</p>
                  </div>
                  <DocumentsVerificationPanel provider={provider} onUpdate={loadProviderData} />
                </div>
              )}
              {activeTab === 'support' && (
                <SupportChat 
                  provider={provider} 
                  onUnreadChange={(count) => setUnreadMessages(count)} 
                />
              )}
              {activeTab === 'notifications' && (
                <NotificationsPanel 
                  provider={provider} 
                  onActionClick={handleNotificationAction}
                />
              )}
              {activeTab === 'payment' && isPaymentPending && (
                <div className="bg-white rounded-xl shadow-sm p-6">
                  <h2 className="text-2xl font-bold text-gray-900 mb-6">Complete Subscription Payment</h2>
                  <div className="max-w-2xl">
                    <div className="bg-orange-50 border-2 border-orange-200 rounded-xl p-6 mb-6">
                      <div className="flex items-start gap-3">
                        <i className="ri-time-line text-orange-600 text-2xl"></i>
                        <div>
                          <h3 className="font-bold text-orange-900 mb-2">Payment Deadline</h3>
                          <p className="text-orange-800 mb-1">
                            You have <strong>{paymentHoursRemaining} hours</strong> remaining to complete your payment.
                          </p>
                          <p className="text-sm text-orange-700">
                            Deadline: <strong>{new Date(provider.payment_deadline).toLocaleString()}</strong>
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Trade-specific pricing */}
                    <SubscriptionPaymentSection 
                      trade={provider.trade}
                      userId={user?.id}
                      profileId={provider.id}
                      onPaymentComplete={checkUser}
                    />
                  </div>
                </div>
              )}
            </div>
          </main>
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {renderContent()}
    </div>
  );
}